import {
    EMAIL_REQUIRED,
    EMAIL_VALID,
    FIRSTNAME_REQUIRED,
    INVALID_MILEAGE_MESSAGE,
    INVALID_REGISTRATION_MESSAGE,
    LASTNAME_REQUIRED,
    MESSAGE_REQUIRED,
    MILEAGE_LENGTH_LIMIT_MESSAGE,
    PHONE_REQUIRED,
    PHONE_VALID,
    POSTCODE_REQUIRED,
    POSTCODE_VALID,
    QUERY_TYPE_REQUIRED,
    SUBJECT_REQUIRED,
    TITLE_REQUIRED,
    VEHICLE_REGISTRATION_REQUIRED,
} from "common/validation/validationMessages";
import { EMAIL_REGEX, PHONE_AND_MOBILE_REGEX } from "lib/constants";
import validatePlateNumber from "utils/validatePlateNumber";
import { validatePostcode } from "utils/validatePostcode";
import { z } from "zod";

const firstName = z.string().min(2, { message: FIRSTNAME_REQUIRED });

const lastName = z.string().min(2, { message: LASTNAME_REQUIRED });

const email = z
    .string()
    .min(1, { message: EMAIL_REQUIRED })
    .regex(EMAIL_REGEX, { message: EMAIL_VALID });

const postCode = z
    .string()
    .min(1, { message: POSTCODE_REQUIRED })
    .refine((v) => validatePostcode(v), { message: POSTCODE_VALID });

export const optionalPostCode = z
    .string()
    .refine((v) => v === "" || validatePostcode(v), { message: POSTCODE_VALID })
    .optional();

const phone = z
    .string()
    .min(11, { message: PHONE_REQUIRED })
    .regex(PHONE_AND_MOBILE_REGEX, { message: PHONE_VALID });

const vehicleRegistration = z
    .string()
    .min(1, { message: VEHICLE_REGISTRATION_REQUIRED })
    .max(8, { message: INVALID_REGISTRATION_MESSAGE })
    .refine((v) => validatePlateNumber(v).isValid, { message: INVALID_REGISTRATION_MESSAGE });

const vehicleRegistrationNotRequired = z
    .string()
    .max(8, { message: INVALID_REGISTRATION_MESSAGE })
    //we want to allow an empty value, but also validate it's a correct number plate if it's there.
    .refine((v) => v === "" || validatePlateNumber(v).isValid, {
        message: INVALID_REGISTRATION_MESSAGE,
    });

const vehicleMileage = z
    .string()
    .min(1, {
        message: INVALID_MILEAGE_MESSAGE,
    })
    .max(6, {
        message: MILEAGE_LENGTH_LIMIT_MESSAGE,
    })
    .refine((v) => +v);

const marketingConsent = z.boolean().optional();

export const ContactFormSchema = z.object({
    title: z.enum(["Mr", "Miss", "Ms", "Mrs", "Dr", "Rev", "Sir", "Lady", "Mx"], {
        errorMap: () => ({ message: TITLE_REQUIRED }),
    }),
    firstName,
    lastName,
    email,
    postCode,
    contactNumber: phone,
    message: z.string().min(10, { message: MESSAGE_REQUIRED }),
    marketingConsent,
});

export const CustomerCareContactFormSchema = z.object({
    firstName,
    lastName,
    email,
    vehicleRegistration: vehicleRegistration,
    queryType: z.enum(["New", "Existing"], {
        errorMap: () => ({ message: QUERY_TYPE_REQUIRED }),
    }),
    subject: z.string().min(2, { message: SUBJECT_REQUIRED }),
    description: z.string().min(10, { message: MESSAGE_REQUIRED }),
});

export const PXYourDetailsFormSchema = z.object({
    firstName,
    lastName,
    emailAddress: email,
    mobile: phone,
    postCode,
    userIsLoggedIn: z.string().default("false"),
    marketingSignUp: marketingConsent,
});

export const DeliveryOrCollectionFormSchema = z.object({ postCode });

export const CustomSignUpBlockSchema = z.object({
    registration: vehicleRegistration,
    mileage: vehicleMileage,
    desired_warranty: z.string().min(1, {
        message: "Please select the warranty you require",
    }),
    phone,
    email,
    marketingConsent,
});

export const SellWidgetSchema = z.object({
    vehicleRegistration: vehicleRegistrationNotRequired,
});

export const StoreWidgetSchema = z.object({ postCode });
